/**
 * Data Change Detector
 * Monitors data changes and triggers background cache updates
 */

import { publishDataChanged } from '../infrastructure/events/legacyEventBridge';

class DataChangeDetector {
  constructor() {
    this.originalMethods = new Map();
    this.pageDependencies = new Map();
    this.isInitialized = false;
    this.eventQueue = [];
    this.isProcessing = false;
    this.throttleDelay = 100; // 100ms throttle
  }

  /**
   * Initialize data change detection
   */
  initialize() {
    if (this.isInitialized) return;

    this.setupPageDependencies();
    this.setupInterceptors();
    this.isInitialized = true;
  }

  /**
   * Setup page dependencies mapping
   */
  setupPageDependencies() {
    this.pageDependencies.set('products', ['inventory', 'sales', 'purchases', 'dashboard']);
    this.pageDependencies.set('sales', ['sales', 'dashboard', 'reports']);
    this.pageDependencies.set('purchases', ['purchases', 'inventory', 'dashboard']);
    this.pageDependencies.set('lots', ['inventory', 'dashboard']);
    this.pageDependencies.set('customers', ['sales', 'dashboard']);
    this.pageDependencies.set('suppliers', ['purchases', 'dashboard']);
    this.pageDependencies.set('categories', ['inventory', 'sales', 'purchases']);
  }

  /**
   * Setup data change interceptors
   */
  setupInterceptors() {
    // This will be called from DataContext to wrap the setters

  }

  /**
   * Wrap data setter methods
   */
  wrapDataSetter(originalSetter, dataType) {
    return (...args) => {
      // Call original method
      const result = originalSetter.apply(this, args);
      
      // Trigger data change event
      this.triggerDataChange(dataType, 'modified');
      
      return result;
    };
  }

  /**
   * Trigger data change event (throttled)
   */
  triggerDataChange(dataType, changeType, businessId = 'default') {
    // Add to queue instead of dispatching immediately
    this.eventQueue.push({
      dataType,
      changeType,
      businessId,
      timestamp: Date.now()
    });

    // Process queue if not already processing
    if (!this.isProcessing) {
      this.processEventQueue();
    }
  }

  /**
   * Process event queue with throttling
   */
  processEventQueue() {
    if (this.isProcessing || this.eventQueue.length === 0) return;

    this.isProcessing = true;

    // Get the latest event for each data type
    const latestEvents = new Map();
    this.eventQueue.forEach(event => {
      latestEvents.set(event.dataType, event);
    });

    // Clear the queue
    this.eventQueue = [];

    // Process each unique data type
    latestEvents.forEach(event => {
      const affectedPages = this.pageDependencies.get(event.dataType) || [];
      
      if (affectedPages.length === 0) return;

      const changeEvent = {
        dataType: event.dataType,
        changeType: event.changeType,
        affectedPages,
        businessId: event.businessId,
        timestamp: event.timestamp
      };

      // Dispatch global event
      publishDataChanged(changeEvent);


    });

    // Reset processing flag after a delay
    setTimeout(() => {
      this.isProcessing = false;
      // Process any new events that came in during processing
      if (this.eventQueue.length > 0) {
        this.processEventQueue();
      }
    }, this.throttleDelay);
  }

  /**
   * Get affected pages for a data type
   */
  getAffectedPages(dataType) {
    return this.pageDependencies.get(dataType) || [];
  }

  /**
   * Add custom page dependency
   */
  addPageDependency(dataType, pageName) {
    const existing = this.pageDependencies.get(dataType) || [];
    if (!existing.includes(pageName)) {
      existing.push(pageName);
      this.pageDependencies.set(dataType, existing);
    }
  }

  /**
   * Remove page dependency
   */
  removePageDependency(dataType, pageName) {
    const existing = this.pageDependencies.get(dataType) || [];
    const filtered = existing.filter(page => page !== pageName);
    this.pageDependencies.set(dataType, filtered);
  }

  /**
   * Get all dependencies
   */
  getAllDependencies() {
    return Object.fromEntries(this.pageDependencies);
  }

  /**
   * Cleanup
   */
  cleanup() {
    this.originalMethods.clear();
    this.pageDependencies.clear();
    this.isInitialized = false;
  }
}

// Create singleton instance
const dataChangeDetector = new DataChangeDetector();

export default dataChangeDetector;
